import { Variant } from 'shared/types/product';
import { isVariantMatching } from 'helpers/utils/variantEligibility';
import { getAttributeLabel } from 'helpers/utils/attributes';
import { FacetFilter } from '../types';

const searchableAttributes = ['color_dd_v1', 'frame_color_wa'];

export const getMatchingVariant = (variants: Variant[], searchQuery?: string, isSearchResult?: boolean) => {
  const normalize = (value: unknown) =>
    String(value ?? '')
      .trim()
      .toLowerCase();

  if (!searchQuery || !isSearchResult) {
    return variants.findIndex((variant) => isVariantMatching(variant));
  }

  const query = normalize(searchQuery);

  const getValues = (variant: Variant) =>
    searchableAttributes.map((key) => getAttributeLabel(variant.attributes?.[key])).filter(Boolean);

  // Exact Match
  const exactIndex = variants.findIndex(
    (variant) => isVariantMatching(variant) && getValues(variant).some((value) => normalize(value) === query),
  );

  if (exactIndex !== -1) {
    return exactIndex;
  }

  // Includes Match
  const includesIndex = variants.findIndex(
    (variant) => isVariantMatching(variant) && getValues(variant).some((value) => normalize(value).includes(query)),
  );

  return includesIndex !== -1 ? includesIndex : 0;
};

const MAX_LIMIT = 40;

export const buildPaginatedQueries = (desiredLimit: number, alreadyFetched: number, queryToCall: any) => {
  if (alreadyFetched >= desiredLimit) return [];
  const remaining = desiredLimit - alreadyFetched;
  const callsCount = Math.ceil(remaining / MAX_LIMIT);

  return Array.from({ length: callsCount }, (_, index) => ({
    ...queryToCall,
    limit: MAX_LIMIT,
    cursor: `offset:${alreadyFetched + index * MAX_LIMIT}`,
  }));
};

export const deriveQuery = (queryToCall: Record<string, any>, currentQuery: string | null) => {
  const currentParsed = currentQuery ? JSON.parse(currentQuery) : null;

  const { limit: newLimit, ...newRest } = queryToCall;
  const { limit: oldLimit, ...oldRest } = currentParsed || {};

  const isSameWithoutLimit = JSON.stringify(newRest) === JSON.stringify(oldRest);

  const isSameWithLimit = currentQuery === JSON.stringify(queryToCall);

  return {
    isSameWithoutLimit,
    isSameWithLimit,
    queryWithoutLimit: JSON.stringify(newRest),
    fullQuery: JSON.stringify(queryToCall),
    newLimit,
    oldLimit,
  };
};

interface FacetInput {
  facetLabel: string;
  facetKey: string;
}

export const buildFacetFilters = (facets: FacetInput[] = []): FacetFilter[] => {
  const seen = new Set<string>();

  return facets.flatMap(({ facetLabel, facetKey }) =>
    facetKey
      .split(',')
      .map((key) => key.trim())
      .filter((key) => {
        if (seen.has(key)) {
          return false;
        }

        seen.add(key);
        return true;
      })
      .map(
        (key): FacetFilter => ({
          key,
          type: 'term',
          label: facetLabel.trim(),
        }),
      ),
  );
};
